import { Inject, Injectable } from '@angular/core';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { ProcedureMap } from '@shared/models/procedure-map';
import { LoggerService } from '@core/services/logger/logger.service';
import { SendToIdForNotLabsEnum, SendToTypeEnum, ShipToForNotLabsEnum } from '../models/send-to-type.enum';
import { IdName } from '@shared/models/id-name';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';
import { OrderQuery } from '@modules/order/state/order-query';
import { RX_GENERAL_RULES } from '@shared/rules/rules-tokens';
import { RxGeneralRules } from '@shared/models/rx-general-rules';
import { TypeEnum } from '@modules/order/models/type.enum';
import { combineQueries } from '@datorama/akita';
import { Procedure } from '@shared/models/procedure';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { AddRxProcedureMapping } from '@shared/models/rx-rules-json-interface';
import { AddRxService } from '@shared/services/add-rx.service';
import { TRANSLATE_ON_DEMAND_PREFIX } from '@shared/pipes/translate-on-demand.pipe';
import { SendToOption } from '@shared/models/send-to-option';
import { getShipToFromOption } from '@shared/utils/send-to-utils';
import { LinkedCompanyDto } from '@shared/models/linked-companies';
import { Maybe } from '@/types/globals';
import { SoftwareOptionsService } from '@shared/services/software-options.service';
import { SoftwareOptions } from '@shared/models/enums/enums';
import { RxForDoctorQuery } from '@modules/rx-for-doctor/state/rx-for-doctor-query';
import { enumEquals } from '@shared/utils/enum-utils';

interface ProcedureTypeSendToIds {
	procedureId: number;
	typeId?: Maybe<number>;
	sendToOption?: SendToOption;
}

@Injectable({
	providedIn: 'root'
})
export class ProcedureConfigurationService {
	private readonly sendToTypeMapper: Record<SendToIdForNotLabsEnum, SendToTypeEnum> = {
		[SendToIdForNotLabsEnum.glidewell]: SendToTypeEnum.chairsideGlidewell,
		[SendToIdForNotLabsEnum.exocad]: SendToTypeEnum.chairsideExocad,
		[SendToIdForNotLabsEnum.midcOnly]: SendToTypeEnum.midcOnly,
		[SendToIdForNotLabsEnum.realGUIDE]: SendToTypeEnum.realGUIDE,
		[SendToIdForNotLabsEnum.sprintRay]: SendToTypeEnum.sprintRay
	};

	private readonly procedureTypesOrder = new Map<number, number>([
		[TypeEnum.Invisalign_Aligners, 1],
		[TypeEnum.Invisalign_First_Aligners, 2],
		[TypeEnum.Invisalign_First_Palatal_Expander, 3],
		[TypeEnum.Vivera_Retainer, 4],
		[TypeEnum.Invisalign_Retainer, 5],
		[TypeEnum.Surgical_guide_Tooth_supported, 6],
		[TypeEnum.Surgical_guide_Tissue_Supported, 7],
		[TypeEnum.Surgical_guide_Bone_supported, 8],
		[TypeEnum.Surgical_planning_only_No_guide, 9],
		[TypeEnum.Partial_Denture_Framework, 10],
		[TypeEnum.Partial_Denture_Acrylic, 11],
		[TypeEnum.Partial_Immediate_Denture, 12],
		[TypeEnum.Full_Immediate_Denture, 13],
		[TypeEnum.Full_Denture_tissue_based, 14],
		[TypeEnum.Full_Denture_implant_based, 15],
		[TypeEnum.Night_guard, 16],
		[TypeEnum.Bite_Splint, 17],
		[TypeEnum.Sports_Mouth_guard, 18],
		[TypeEnum.Apnea_Sleep_Appliance, 19],
		[TypeEnum.Ortho_Appliance, 20]
	]);

	private readonly sendToTypeOrder = new Map<number, number>([
		[SendToTypeEnum.chairsideExocad, 1],
		[SendToTypeEnum.chairsideGlidewell, 2],
		[SendToTypeEnum.realGUIDE, 3],
		[SendToTypeEnum.sprintRay, 4],
		[SendToTypeEnum.orthoLab, 5],
		[SendToTypeEnum.restoLab, 6],
		[SendToTypeEnum.midcOnly, 7]
	]);

	procedures$: Observable<Procedure[]> = combineQueries([
		this.orderQuery.availableProcedureMaps$,
		this.shellQuery.procedures$,
		this.shellQuery.isCloningMode$,
		this.shellQuery.enableAllCaseTypesForAddRx$,
		this.addRxService.clonedRxProcedureRule$
	]).pipe(
		map(args => this.getAvailableProcedures(...args)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	types$: Observable<IdName[]> = combineQueries([this.orderQuery.availableProcedureMaps$, this.orderQuery.procedureMap$]).pipe(
		filter((values): values is [ProcedureMap[], ProcedureMap] => !!values[1]),
		map(([availableProcedureMaps, procedureMap]) => this.getTypes(availableProcedureMaps, procedureMap.ProcedureId)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	sendToOptions$: Observable<Maybe<SendToOption[]>> = combineQueries([
		this.orderQuery.procedure$,
		this.orderQuery.type$,
		this.shellQuery.companies$
	]).pipe(
		filter((values): values is [Procedure, Maybe<IdName>, IdName[]] => {
			const [procedure, , linkedCompanies] = values;

			return !!procedure && !!linkedCompanies;
		}),
		map(([procedure, type]) => this.getAvailableLabs({ procedureId: procedure.Id, typeId: type?.Id })),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	/** Send-to options for the selected procedure regardless of the selected procedure type. */
	procedureSendToOptions$: Observable<SendToOption[] | null> = combineQueries([
		this.orderQuery.procedure$,
		this.shellQuery.companies$
	]).pipe(
		filter((values): values is [Procedure, IdName[]] => {
			const [procedure, linkedCompanies] = values;

			return !!procedure && !!linkedCompanies;
		}),
		map(([procedure]) => this.getAvailableLabs({ procedureId: procedure.Id })),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	defaultProcedure$: Observable<Procedure | undefined> = combineLatest({
		procedureId: this.shellQuery.defaultProcedure$,
		procedures: this.procedures$,
		isNewRx: this.shellQuery.isNewAndNotClonedRx$,
		isReferralWorkflowPractice: this.rxForDoctorQuery.isReferralWorkflowPractice$
	}).pipe(
		filter(({ isNewRx }) => isNewRx),
		map(({ procedureId, procedures, isReferralWorkflowPractice }) => {
			return isReferralWorkflowPractice
				? procedures.find(x => enumEquals(x.Id, ProcedureEnum.Invisalign))
				: procedures.find(x => x.Id === procedureId);
		}),
		distinctUntilChanged()
	);

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly orderQuery: OrderQuery,
		private readonly logger: LoggerService,
		private readonly addRxService: AddRxService,
		private readonly rxForDoctorQuery: RxForDoctorQuery,
		@Inject(RX_GENERAL_RULES) private readonly rxGeneralRules: RxGeneralRules
	) {}

	getAvailableProcedures(
		procedureMaps: Maybe<ProcedureMap[]>,
		procedures: Procedure[],
		isCloningMode: boolean,
		enableAllCaseTypesForAddRx: boolean,
		addRxRule: Maybe<AddRxProcedureMapping>
	): Procedure[] {
		if (!procedureMaps?.length || !procedures.length) {
			return [];
		}

		let sortedProcedures = this.getSortedProcedures(procedures);

		if (!!isCloningMode && !enableAllCaseTypesForAddRx) {
			sortedProcedures = addRxRule
				? sortedProcedures.filter(({ Id }) => addRxRule.AvailableProcedures.some(procedureId => enumEquals(Id, procedureId)))
				: sortedProcedures.filter(({ AddRxAvailable }) => !!AddRxAvailable);
		}

		return sortedProcedures.filter(({ Id }) => procedureMaps.some(({ ProcedureId }) => Id === ProcedureId));
	}

	getProcedureMap(procedureId: number, typeId?: Maybe<number>, sendToOption?: Maybe<SendToOption>): ProcedureMap | undefined {
		let procedureMap: ProcedureMap | undefined;

		if (typeId === null || typeId === undefined) {
			// use default or empty typeId
			typeId = this.rxGeneralRules.DefaultTypes.find(x => x.ProcedureId === procedureId)?.TypeId ?? TypeEnum.Empty;
			procedureMap = this.orderQuery.availableProcedureMaps.find(x =>
				this.checkProcedureMap(x, { procedureId, typeId, sendToOption: sendToOption ?? undefined })
			);

			if (procedureMap) {
				return procedureMap;
			}
			typeId = undefined; // allow first available typeId
		}

		procedureMap = this.orderQuery.availableProcedureMaps.find(pm =>
			this.checkProcedureMap(pm, { procedureId, typeId, sendToOption: sendToOption ?? undefined })
		);
		if (!procedureMap) {
			this.logger.error(`getProcedureMapItem: no procedure map for: ${JSON.stringify({ procedureId, typeId, sendToOption })}`, {
				module: 'ProcedureConfigurationService'
			});
			procedureMap = this.orderQuery.availableProcedureMaps.find(x =>
				this.checkProcedureMapByProcedureAndType(x, { procedureId, typeId, sendToOption: sendToOption ?? undefined })
			);
		}

		return procedureMap;
	}

	/**
	 * Procedure map of the current procedure for the given send-to.
	 * Send-to options ignore the procedure type, so the lab may not support the current type. In that case the type is cleared:
	 * the lab's empty-type map is preferred, otherwise its default/first map.
	 */
	getProcedureMapForSendTo(procedureMap: ProcedureMap, sendTo: SendToOption | null | undefined): ProcedureMap | undefined {
		const { ProcedureId: procedureId, TypeId: typeId } = procedureMap;

		return this.getProcedureMap(procedureId, this.resolveTypeIdForSendTo(procedureId, typeId, sendTo), sendTo);
	}

	getAvailableLabs({ procedureId, typeId }: { procedureId: number; typeId?: number }): SendToOption[] | null {
		let availableLabs: SendToOption[] = [];

		const procedureMaps = this.orderQuery.availableProcedureMaps.filter(x =>
			this.checkProcedureMapByProcedureAndType(x, { procedureId, typeId })
		);

		if (this.shellQuery.companiesBySendToType && Object.keys(this.shellQuery.companiesBySendToType).length > 0) {
			availableLabs.push(...this.getLabsFromLinkedCompanies(procedureMaps));
		} else {
			availableLabs.push(...this.getLabsFromStaticMapping(procedureMaps));
		}

		availableLabs.push(...this.getSendToForUnknownProcedureMaps(procedureMaps));

		availableLabs = this.getUniqueLabs(availableLabs);

		return availableLabs.length > 0 ? availableLabs : null;
	}

	getSendToForUnknownProcedureMaps(procedureMaps: ProcedureMap[]): SendToOption[] {
		if (
			SoftwareOptionsService.checkSoftwareOptions(this.shellQuery.softwareOptionsForCompany ?? [], [
				SoftwareOptions.RxShowUnknownProcedureMaps
			])
		) {
			return procedureMaps
				.filter(pm => !this.isKnownSendToType(pm.SendToTypes))
				.map(pm => ({
					Id: `unknownProcedureMap${pm.Id}sendToType${pm.SendToTypes}`,
					Name: pm.Comment ?? `Special type ${pm.SendToTypes.toString()}`,
					SendToTypes: pm.SendToTypes,
					ShipTo: ShipToForNotLabsEnum.midcOnly
				}));
		}

		return [];
	}

	getSendToIdForNotLabs(sendToType: Maybe<number>): SendToOption | null {
		const id = parseInt(
			Object.keys(this.sendToTypeMapper).find(
				typeMapperKey => this.sendToTypeMapper[typeMapperKey as unknown as SendToIdForNotLabsEnum] === sendToType
			) ?? '',
			10
		);

		if (!id) {
			return null;
		}

		const terms: Record<number, string> = {
			[SendToTypeEnum.chairsideExocad]: 'OrderSection.ChairSide',
			[SendToTypeEnum.chairsideGlidewell]: 'OrderSection.Glideweill',
			[SendToTypeEnum.midcOnly]: 'OrderSection.MidcOnly',
			[SendToTypeEnum.realGUIDE]: 'OrderSection.RealGuide',
			[SendToTypeEnum.sprintRay]: 'OrderSection.SprintRay'
		};

		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		return { Id: id, Name: `${TRANSLATE_ON_DEMAND_PREFIX}${terms[sendToType!]}` };
	}

	getTypes(availableProcedureMaps: ProcedureMap[], procedureId: number): IdName[] {
		if (!availableProcedureMaps || !this.shellQuery.rxConfiguration?.Types) {
			this.logger.error(
				`Available procedure maps is missing or rx configuration is missing types ${JSON.stringify(
					this.shellQuery.rxConfiguration
				)}`,
				{
					module: 'procedure-configuration.service'
				}
			);

			return [];
		}

		const typeIds = availableProcedureMaps?.filter(x => x.ProcedureId === procedureId).map(x => x.TypeId);

		if (typeIds.length === 0) {
			return [];
		}

		return this.sortOptions(
			this.shellQuery.rxConfiguration?.Types?.filter(x => typeIds.includes(x.Id)),
			this.procedureTypesOrder
		);
	}

	private resolveTypeIdForSendTo(procedureId: number, typeId: number, sendTo: SendToOption | null | undefined): number | undefined {
		if (!sendTo || this.isLabAvailableForType(sendTo, procedureId, typeId)) {
			return typeId;
		}

		return this.isLabAvailableForType(sendTo, procedureId, TypeEnum.Empty) ? TypeEnum.Empty : undefined;
	}

	private isLabAvailableForType(sendTo: SendToOption, procedureId: number, typeId: number): boolean {
		return this.getAvailableLabs({ procedureId, typeId })?.some(lab => lab.Id === sendTo.Id) ?? false;
	}

	private isKnownSendToType(sendToType: number): boolean {
		const knownTypesFromEnum = (Object.values(SendToTypeEnum) as number[]).includes(sendToType);
		const knownTypeFromLinkedCompanies = !!this.shellQuery.companiesBySendToType?.[sendToType as SendToTypeEnum];

		return knownTypesFromEnum || knownTypeFromLinkedCompanies;
	}

	private checkProcedureMap(procedureMap: ProcedureMap, checkedValues: ProcedureTypeSendToIds): boolean {
		return (
			this.checkProcedureMapByProcedureAndType(procedureMap, checkedValues) &&
			((!checkedValues.sendToOption && procedureMap.SendToTypes === SendToTypeEnum.empty) ||
				this.getSendToTypes(checkedValues.sendToOption).includes(procedureMap.SendToTypes))
		);
	}

	private checkProcedureMapByProcedureAndType(procedureMap: ProcedureMap, { procedureId, typeId }: ProcedureTypeSendToIds) {
		return procedureMap.ProcedureId === procedureId && (typeId === undefined || typeId === null || procedureMap.TypeId === typeId);
	}

	private getSendToTypes(sendToOption: Maybe<SendToOption>): number[] {
		// Do not fallback to use sendToOption.Id as sendToType here
		if (sendToOption && 'SendToTypes' in sendToOption && sendToOption.SendToTypes) {
			return [sendToOption.SendToTypes];
		}

		const shipToId = getShipToFromOption(sendToOption);
		const result = [];

		if (this.hasOrthoLab(shipToId)) {
			result.push(SendToTypeEnum.orthoLab);
		}

		if (this.hasRestoLab(shipToId)) {
			result.push(SendToTypeEnum.restoLab);
		}

		return result.length > 0 ? result : [this.sendToTypeMapper[shipToId as SendToIdForNotLabsEnum]];
	}

	private hasOrthoLab(shipToId: Maybe<number>): boolean {
		const orthoLabs: Maybe<IdName[]> = this.shellQuery.companiesBySendToType?.[SendToTypeEnum.orthoLab] ?? this.shellQuery.orthoLabs;

		return orthoLabs?.some(x => x.Id === shipToId) ?? false;
	}

	private hasRestoLab(shipToId: Maybe<number>): boolean {
		const restoLabs: Maybe<IdName[]> = this.shellQuery.companiesBySendToType?.[SendToTypeEnum.restoLab] ?? this.shellQuery.dentalLabs;

		return restoLabs?.some(x => x.Id === shipToId) ?? false;
	}

	private getUniqueLabs(labs: SendToOption[]): SendToOption[] {
		const uniqueLabs = new Map<SendToOption['Id'], SendToOption>(labs.map(v => [v.Id, v]));

		return Array.from(uniqueLabs.values());
	}

	private getSortedProcedures(procedures: Procedure[]): Procedure[] {
		const sortingOrder = new Map<number, number>([
			[ProcedureEnum.StudyModel_iRecord, 1],
			[ProcedureEnum.InvisalignART, 2],
			[ProcedureEnum.Invisalign, 3],
			[ProcedureEnum.FixedRestorative, 4],
			[ProcedureEnum.ImplantPlanning, 5],
			[ProcedureEnum.Denture_Removable, 6],
			[ProcedureEnum.Appliance, 7]
		]);

		return this.sortOptions(procedures, sortingOrder);
	}

	private getLabsFromStaticMapping(procedureMaps: ProcedureMap[]): SendToOption[] {
		const availableLabs: SendToOption[] = [];
		const sendToTypes = procedureMaps.map(x => x.SendToTypes);

		if (sendToTypes.includes(SendToTypeEnum.chairsideExocad)) {
			availableLabs.push(this.getSendToIdForNotLabs(SendToTypeEnum.chairsideExocad) as SendToOption);
		}

		if (sendToTypes.includes(SendToTypeEnum.chairsideGlidewell)) {
			availableLabs.push(this.getSendToIdForNotLabs(SendToTypeEnum.chairsideGlidewell) as SendToOption);
		}

		if (sendToTypes.includes(SendToTypeEnum.realGUIDE)) {
			availableLabs.push(this.getSendToIdForNotLabs(SendToTypeEnum.realGUIDE) as SendToOption);
		}

		if (sendToTypes.includes(SendToTypeEnum.sprintRay)) {
			availableLabs.push(this.getSendToIdForNotLabs(SendToTypeEnum.sprintRay) as SendToOption);
		}

		if (sendToTypes.includes(SendToTypeEnum.orthoLab) && this.shellQuery.orthoLabs) {
			availableLabs.push(...this.shellQuery.orthoLabs);
		}

		if (sendToTypes.includes(SendToTypeEnum.restoLab) && this.shellQuery.dentalLabs) {
			availableLabs.push(...this.shellQuery.dentalLabs);
		}

		if (sendToTypes.includes(SendToTypeEnum.midcOnly)) {
			availableLabs.push(this.getSendToIdForNotLabs(SendToTypeEnum.midcOnly) as SendToOption);
		}

		return availableLabs;
	}

	private getLabsFromLinkedCompanies(procedureMaps: ProcedureMap[]): SendToOption[] {
		const availableLabs: SendToOption[] = [];
		const sendToTypes = this.getSortedSendToTypes(procedureMaps);
		const availableProcedureMaps = new Set(procedureMaps.map(m => m.Id));

		sendToTypes.forEach(sendToType => {
			const labOptions: SendToOption[] = this.getSendToOptions(sendToType, availableProcedureMaps);
			const notLabOption = this.getSendToIdForNotLabs(sendToType);

			if (notLabOption) {
				availableLabs.push(notLabOption);
			}

			availableLabs.push(...labOptions);
		});

		return availableLabs;
	}

	private getSortedSendToTypes(procedureMaps: ProcedureMap[]): Set<number> {
		const sendToTypesOptions = procedureMaps.map(x => ({ Id: x.SendToTypes, Name: x.Comment }));
		const sorted = this.sortOptions(sendToTypesOptions, this.sendToTypeOrder);

		return new Set(sorted.map(c => c.Id));
	}

	private sortOptions<T extends IdName>(options: T[], sortingOrder: Map<number, number>): T[] {
		const sortedOptions = [...options];

		return sortedOptions.sort(
			(p1, p2) => (sortingOrder.get(p1.Id) ?? Number.MAX_SAFE_INTEGER) - (sortingOrder.get(p2.Id) ?? Number.MAX_SAFE_INTEGER)
		);
	}

	/**
	 * Gets send to options (labs) filtered by available procedure maps from linked companies.
	 * The linked company will only show up in SendTo list if it itself has the selected procedure map available to it.
	 * @param sendToTypeId Send to type ID (e.g., ortho lab, resto lab).
	 * @param availableProcedureMaps Set of procedure map IDs available for the current procedure.
	 * @returns Array of send to options (labs) that support the available procedure maps.
	 */
	private getSendToOptions(sendToTypeId: number, availableProcedureMaps: Set<number>): SendToOption[] {
		const companies: LinkedCompanyDto[] = this.shellQuery.companiesBySendToType?.[sendToTypeId as SendToTypeEnum] ?? [];
		const companiesSupportingProcedures = companies.filter(
			company => !company.LimitedToProcedureMaps || company.LimitedToProcedureMaps.some(id => availableProcedureMaps.has(id))
		);

		return companiesSupportingProcedures.map(
			({ Id, Name, Settings }) =>
				({
					Id,
					Name,
					SendToTypes: sendToTypeId,
					...(Settings && { Settings })
				}) as SendToOption
		);
	}
}
